import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  ReactNode,
} from "react";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { useAuth } from "@/contexts/AuthContext";
import type { Plan, SubscriptionStatus } from "@/types";

const FREE_GRAMMAR_LIMIT = 10;

type ProFeature = "grammar" | "vocabulary" | "categorize" | "llmTranslate";

interface SubscriptionContextType {
  plan: Plan;
  isPro: boolean;
  subscriptionStatus: SubscriptionStatus;
  currentPeriodEnd: Date | null;
  grammarUsageCount: number;
  grammarUsageRemaining: number;
  canUseFeature: (feature: ProFeature) => boolean;
  incrementGrammarUsage: () => void;
  showUpgradePrompt: boolean;
  setShowUpgradePrompt: (show: boolean) => void;
  upgradeFeature: ProFeature | null;
  openUpgradePrompt: (feature?: ProFeature) => void;
  closeUpgradePrompt: () => void;
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(
  undefined
);

export function SubscriptionProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [plan, setPlan] = useState<Plan>("free");
  const [subscriptionStatus, setSubscriptionStatus] =
    useState<SubscriptionStatus>("none");
  const [currentPeriodEnd, setCurrentPeriodEnd] = useState<Date | null>(null);
  const [grammarUsageCount, setGrammarUsageCount] = useState(0);
  const [, setGrammarResetAt] = useState<Date | null>(null);
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);
  const [upgradeFeature, setUpgradeFeature] = useState<ProFeature | null>(null);

  // Listen to user doc for real-time subscription changes
  useEffect(() => {
    if (!user?.uid) {
      setPlan("free");
      setSubscriptionStatus("none");
      setCurrentPeriodEnd(null);
      setGrammarUsageCount(0);
      setGrammarResetAt(null);
      return;
    }

    const unsubscribe = onSnapshot(
      doc(db, "users", user.uid),
      (snapshot) => {
        const data = snapshot.data();
        if (!data) return;

        setPlan(data.plan || "free");
        setSubscriptionStatus(data.stripeSubscriptionStatus || "none");
        setCurrentPeriodEnd(
          data.stripeCurrentPeriodEnd?.toDate?.() || null
        );

        // Reset grammar count if the reset period has passed
        const resetAt = data.grammarAnalysisResetAt?.toDate?.();
        if (resetAt && resetAt < new Date()) {
          // Count has been reset server-side or needs resetting
          setGrammarUsageCount(0);
          setGrammarResetAt(null);
        } else {
          setGrammarUsageCount(data.grammarAnalysisCount || 0);
          setGrammarResetAt(resetAt || null);
        }
      }
    );

    return () => unsubscribe();
  }, [user?.uid]);

  const isPro = plan === "pro";

  const grammarUsageRemaining = isPro
    ? Infinity
    : Math.max(0, FREE_GRAMMAR_LIMIT - grammarUsageCount);

  const canUseFeature = useCallback(
    (feature: ProFeature): boolean => {
      if (isPro) return true;
      if (feature === "grammar") return grammarUsageRemaining > 0;
      // vocabulary, categorize, llmTranslate are pro-only
      return false;
    },
    [isPro, grammarUsageRemaining]
  );

  const openUpgradePrompt = useCallback((feature?: ProFeature) => {
    setUpgradeFeature(feature || null);
    setShowUpgradePrompt(true);
  }, []);

  // Expose for E2E tests (dev only, tree-shaken in production builds)
  if (import.meta.env.DEV) {
    (window as any).__e2e_openUpgradePrompt = openUpgradePrompt;
  }

  const closeUpgradePrompt = useCallback(() => {
    setShowUpgradePrompt(false);
    setUpgradeFeature(null);
  }, []);

  const incrementGrammarUsage = useCallback(() => {
    setGrammarUsageCount((prev) => prev + 1);
  }, []);

  const value: SubscriptionContextType = {
    plan,
    isPro,
    subscriptionStatus,
    currentPeriodEnd,
    grammarUsageCount,
    grammarUsageRemaining,
    canUseFeature,
    incrementGrammarUsage,
    showUpgradePrompt,
    setShowUpgradePrompt,
    upgradeFeature,
    openUpgradePrompt,
    closeUpgradePrompt,
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
}

export function useSubscription() {
  const context = useContext(SubscriptionContext);
  if (context === undefined) {
    throw new Error(
      "useSubscription must be used within a SubscriptionProvider"
    );
  }
  return context;
}
